/* eslint-disable max-lines */
/* eslint-disable react/jsx-max-depth */
import { Card, Radio, Loader } from '@mantine/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import Button from '../../elements/Button';
import ButtonLink from '../../elements/ButtonLink';
import Text from '../../fields/Text';
import IcHistory from '../../icons/IcHistory';
import { validate } from './schema';
import styles from './styles.scoped.css';
import { Editor } from '@tinymce/tinymce-react';
import TextArea from '../../fields/TextArea';
import { TINYAPI } from '../../../../env';
import { format, parseISO } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

const RadioField = ({ input, options }) => (
  <Radio.Group
    {...input}
    className={styles['horizontal-radio']}
    onChange={input.onChange}
    orientation="horizontal"
    spacing="sm"
    styles={{
      root: { display: 'flex', alignItems: 'center', height: '36px' },
    }}
  >
    {options.map((option) => (
      <Radio
        key={option.value}
        label={option.label}
        styles={{
          inner: { display: 'flex', alignItems: 'center' },
          body: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            height: '100%',
          },
          radio: { margin: 0, transform: 'translateY(0)' },
          label: {
            paddingLeft: 4,
            display: 'flex',
            alignItems: 'center',
            marginBottom: 0,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            height: '100%',
          },
        }}
        value={option.value}
      />
    ))}
  </Radio.Group>
);

RadioField.propTypes = {
  input: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

const HistoryItem = ({
  id,
  version,
  createdAt,
  onClick,
  isSelected,
  // createdBy = '-',
}) => {
  let formattedDate = '';

  try {
    if (createdAt) {
      const dateObj = parseISO(createdAt);

      const day = format(dateObj, 'dd', { locale: idLocale });
      const month = format(dateObj, 'MMM', { locale: idLocale });
      const year = format(dateObj, 'yyyy', { locale: idLocale });
      const time = format(dateObj, 'HH:mm', { locale: idLocale });

      formattedDate = `${day} ${month} ${year} | ${time} WIB`;
    }
  } catch (error) {
    formattedDate = 'Format tanggal salah';
  }

  return (
    <div
      className={`${styles['history-item']} ${isSelected ? styles['history-item-selected'] : ''}`}
      data-testid={`history-item-${id}`}
      data-version={version}
      onClick={() => onClick(id)}
    >
      <div className={styles['history-version']}>Versi {version}</div>
      <div className={styles['history-date']}>{formattedDate}</div>
    </div>
  );
};

HistoryItem.propTypes = {
  createdAt: PropTypes.string.isRequired,
  createdBy: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  version: PropTypes.string.isRequired,
};

export function Component({
  error = '',
  // errorUpload = '',
  hasMoreHistory = false,
  initialData = null,
  isLoadingHistory = false,
  onBackClick = null,
  onHistoryItemClick = () => { },
  onLoadMore = () => { },
  selectedVersionId = null,
  uploadImageCallBack = () => { },
  versionHistory = [],
  change,
  handleSubmit,
  submitting,
}) {
  const { dataTermCondition } = useSelector((s) => s.termCondition);

  const hasHistoryData =
    Array.isArray(versionHistory) && versionHistory.length > 0;

  const title = {
    placeholder: '',
  };

  const content = {
    placeholder: '',
    label: 'Deskripsi',
  };

  const version = {
    placeholder: '',
  };

  const [isCodeMode, setIsCodeMode] = useState(false);
  const [contentHtml, setContentHtml] = useState(
    initialData?.content || dataTermCondition?.content || ''
  );
  const [editorKey, setEditorKey] = useState(0);
  const editorRef = useRef(null);

  const _handleEditorChange = (e) => {
    const val = e.target.getContent();
    setContentHtml(val);
    change('content', val);
  };

  const handleCodeModeChange = (e) => {
    const val = e.target.value;
    setContentHtml(val);
    change('content', val);
  };

  const toggleCodeMode = () => {
    if (!isCodeMode) {
      // Switching TO code mode: ambil konten terbaru dari TinyMCE
      if (editorRef.current) {
        const html = editorRef.current.getContent();
        setContentHtml(html);
        change('content', html);
      }
    } else {
      // Switching BACK to visual mode: increment key agar TinyMCE remount
      // dengan contentHtml terbaru sebagai initialValue
      setEditorKey((prev) => prev + 1);
    }
    setIsCodeMode((prev) => !prev);
  };

  useEffect(() => {
    if (initialData) {
      change('title', initialData.title);
      change('content', initialData.content);
      change('version', initialData.version || '');
      change(
        'isEnabled',
        initialData.isEnabled || initialData.isEnable ? 'true' : 'false'
      );
      change('isRequired', initialData.isRequired ? 'true' : 'false');
    } else {
      change('title', dataTermCondition?.title);
      change('content', dataTermCondition?.content);
      change('version', dataTermCondition?.version || '');
      change(
        'isEnabled',
        dataTermCondition?.isEnabled || dataTermCondition?.isEnable
          ? 'true'
          : 'false'
      );
      change('isRequired', dataTermCondition?.isRequired ? 'true' : 'false');
    }
  }, [initialData, dataTermCondition]);

  const isSameId = (id1, id2) => {
    if (
      id1 === null ||
      id2 === null ||
      id1 === undefined ||
      id2 === undefined
    ) {
      return false;
    }
    return String(id1) === String(id2);
  };

  const effectiveSelectedId =
    selectedVersionId || (hasHistoryData ? versionHistory[0].id : null);

  return (
    <div className={styles.container}>
      <div className={styles['form-container']}>
        <Card className={styles.card}>
          <form onSubmit={handleSubmit}>
            <div className={styles['form-row']}>
              <div className={styles['input-group-half']}>
                <label>Judul</label>
                <Field component={Text} inputProps={title} name="title" />
              </div>
              <div className={styles['input-group-half']}>
                <label>Versi</label>
                <Field component={Text} inputProps={version} name="version" />
              </div>
            </div>
            <div className={styles['form-row']}>
              <div className={styles['input-group-half']}>
                <label>Status</label>
                <Field
                  component={RadioField}
                  name="isEnabled"
                  options={[
                    { value: 'true', label: 'Aktif' },
                    { value: 'false', label: 'Tidak Aktif' },
                  ]}
                />
              </div>
              <div className={styles['input-group-half']}>
                <label>Jenis Persetujuan?</label>
                <Field
                  component={RadioField}
                  name="isRequired"
                  options={[
                    { value: 'true', label: 'Wajib' },
                    { value: 'false', label: 'Opsional' },
                  ]}
                />
              </div>
            </div>
            <div className={styles['input-group']}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ margin: 0 }}>Deskripsi</label>
                <Button
                  onClick={toggleCodeMode}
                  size="small"
                  style={isCodeMode ? 'danger' : 'blue'}
                  type="button"
                >
                  {isCodeMode ? 'Mode Visual' : 'Mode Code HTML'}
                </Button>
              </div>

              {isCodeMode ? (
                <div style={{ position: 'relative' }}>
                  <div style={{
                    background: '#1e1e2e',
                    borderRadius: 8,
                    border: '1.5px solid #444',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  }}>
                    <div style={{
                      background: '#181825',
                      padding: '6px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderBottom: '1px solid #333',
                    }}>
                      <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                      <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
                      <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                      <span style={{ color: '#888', fontSize: 12, marginLeft: 8, fontFamily: 'monospace' }}>HTML Code Editor</span>
                    </div>
                    <textarea
                      onChange={handleCodeModeChange}
                      placeholder="Tulis atau paste kode HTML di sini..."
                      spellCheck={false}
                      style={{
                        width: '100%',
                        minHeight: 360,
                        background: '#1e1e2e',
                        color: '#cdd6f4',
                        fontFamily: '"Fira Code", "Consolas", "Courier New", monospace',
                        fontSize: 14,
                        lineHeight: 1.7,
                        border: 'none',
                        outline: 'none',
                        resize: 'vertical',
                        padding: '16px 18px',
                        boxSizing: 'border-box',
                        display: 'block',
                        tabSize: 2,
                      }}
                      value={contentHtml}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                    <span style={{ color: '#888', fontSize: 12 }}>
                      {contentHtml.length} karakter
                    </span>
                  </div>
                </div>
              ) : (
                <Editor
                  apiKey={TINYAPI}
                  init={{
                    height: 405,
                    menubar: true,
                    config: {},
                    images_upload_credentials: true,
                    plugins: [
                      'advlist autolink lists link image charmap print preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount',
                    ],
                    toolbar: `link code image | formatselect | bold italic backcolor | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | removeformat | help`,
                    image_title: true,
                    automatic_uploads: true,
                    file_picker_types: 'image',
                    images_upload_handler: uploadImageCallBack,
                  }}
                  initialValue={contentHtml}
                  key={editorKey}
                  onChange={_handleEditorChange}
                  onInit={(evt, editor) => { editorRef.current = editor; }}
                >
                  <Field
                    component={TextArea}
                    inputProps={content}
                    name="content"
                  />
                </Editor>
              )}
              {error && <span style={{ color: '#EA2036' }}>{error}</span>}
            </div>
            <div className={styles['form-action']}>
              {onBackClick ? (
                <Button
                  className={styles['action-btn-back']}
                  onClick={onBackClick}
                  outlined={true}
                  type="button"
                >
                  Kembali
                </Button>
              ) : (
                <ButtonLink
                  className={styles['action-btn-back']}
                  outlined={true}
                  to={'/term-condition'}
                >
                  Kembali
                </ButtonLink>
              )}
              <Button disabled={submitting} type="submit">
                Simpan
              </Button>
            </div>
          </form>
        </Card>
      </div>
      <div className={styles['history-panel']}>
        <div className={styles['history-title']}>
          <div className={styles['title-content']}>
            <IcHistory className={styles['history-icon']} />
            <span>
              Riwayat
              <br />
              Pembaruan
            </span>
          </div>
        </div>
        <div className={styles['history-list']}>
          {isLoadingHistory && (
            <div className={styles['history-loading']}>
              <Loader size="sm" />
            </div>
          )}

          {!isLoadingHistory && !hasHistoryData && (
            <div className={styles['history-empty']}>
              Tidak ada riwayat pembaruan
            </div>
          )}

          {!isLoadingHistory && hasHistoryData && (
            <>
              {versionHistory.map((item, index) => (
                <HistoryItem
                  createdAt={item.createdAt}
                  createdBy={item.createdBy}
                  id={item.id}
                  isSelected={isSameId(item.id, effectiveSelectedId)}
                  key={`history-item-${item.id}-${index}`}
                  onClick={onHistoryItemClick || (() => { })}
                  version={item.version === 'null' ? '-' : item.version}
                />
              ))}

              {hasMoreHistory && (
                <div className={styles['load-more']} onClick={onLoadMore}>
                  Load more...
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

Component.propTypes = {
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  errorUpload: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  hasMoreHistory: PropTypes.bool,
  initialData: PropTypes.object,
  isLoadingHistory: PropTypes.bool,
  onBackClick: PropTypes.func,
  onHistoryItemClick: PropTypes.func,
  onLoadMore: PropTypes.func,
  selectedVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  submitting: PropTypes.bool.isRequired,
  uploadImageCallBack: PropTypes.func,
  versionHistory: PropTypes.array,
};

export default reduxForm({
  form: 'termCondition',
  initialValues: {
    content: '',
    title: '',
    version: '',
    isEnabled: 'true',
    isRequired: 'true',
  },
  validate,
})(Component);
